/**
 * NotificationPreferences Component
 * 
 * Settings page for push notification preferences.
 * Allows users to toggle each notification type on/off.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Bell,
  MessageSquare,
  UserPlus,
  MessageCircle,
  Clock,
  Megaphone,
  RefreshCw,
  Save,
  TestTube
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const NotificationPreferences = () => {
  const { token } = useAuth();
  const [preferences, setPreferences] = useState({
    leads: true,
    messages: true,
    comments: true,
    reminders: true,
    broadcasts: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPrefs, setOriginalPrefs] = useState(null);

  const fetchPreferences = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/push-notifications/preferences?token=${token}`);
      const data = await response.json();
      
      if (data.success && data.preferences) {
        const prefs = {
          leads: data.preferences.leads ?? true,
          messages: data.preferences.messages ?? true,
          comments: data.preferences.comments ?? true,
          reminders: data.preferences.reminders ?? true,
          broadcasts: data.preferences.broadcasts ?? true
        };
        setPreferences(prefs);
        setOriginalPrefs(prefs);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const savePreferences = async () => {
    setSaving(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/push-notifications/preferences?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Notification preferences saved');
        setOriginalPrefs(preferences);
        setHasChanges(false);
      } else {
        toast.error(data.detail || 'Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error saving preferences');
    } finally {
      setSaving(false);
    }
  };

  const sendTestNotification = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/push-notifications/test?token=${token}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Test notification sent! Check your device.');
      } else {
        toast.error(data.message || 'Failed to send test notification');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Error sending test notification');
    }
  };

  const handleToggle = (key) => {
    const newPrefs = { ...preferences, [key]: !preferences[key] };
    setPreferences(newPrefs);
    
    // Check if different from original
    const changed = JSON.stringify(newPrefs) !== JSON.stringify(originalPrefs);
    setHasChanges(changed);
  };

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const notificationTypes = [
    {
      key: 'leads',
      label: 'Lead Notifications',
      description: 'New lead assignments and lead status changes',
      icon: UserPlus
    },
    {
      key: 'messages',
      label: 'Message Notifications',
      description: 'New chat messages and conversations',
      icon: MessageSquare
    },
    {
      key: 'comments',
      label: 'Comment Notifications',
      description: 'Comments on leads and projects',
      icon: MessageCircle
    },
    {
      key: 'reminders',
      label: 'Reminder Notifications',
      description: 'Follow-up and meeting reminders',
      icon: Clock
    },
    {
      key: 'broadcasts',
      label: 'Broadcast Announcements',
      description: 'Messages from management and team announcements',
      icon: Megaphone
    }
  ];

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading preferences...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="notification-preferences">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Push Notification Settings
            </CardTitle>
            <CardDescription>
              Manage which notifications you receive on your mobile device
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={sendTestNotification}>
            <TestTube className="h-4 w-4 mr-2" />
            Send Test
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {notificationTypes.map(({ key, label, description, icon: Icon }) => (
            <div 
              key={key}
              className="flex items-center justify-between py-3 border-b last:border-0"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-muted rounded-lg">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <Label htmlFor={key} className="text-base font-medium cursor-pointer">
                    {label}
                  </Label>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
              <Switch
                id={key}
                checked={preferences[key]}
                onCheckedChange={() => handleToggle(key)}
              />
            </div>
          ))}
        </div>

        {hasChanges && (
          <div className="mt-6 pt-6 border-t flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setPreferences(originalPrefs);
                setHasChanges(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={savePreferences} disabled={saving}>
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        )}

        {/* Info card about Firebase setup */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
          <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
            Push Notifications Setup
          </h4>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Push notifications require Firebase Cloud Messaging to be configured. 
            Contact your administrator if you're not receiving notifications on your device.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationPreferences;
